import {computed, type Ref} from "vue";
import {useQuery} from "@tanstack/vue-query";
import {fetchWrapper} from "@/helpers/fetch-wrapper";
import {useSessionStore} from "@/stores/session-store";
import type {ApiError} from "@/helpers/api-error";
import type {
  ServiceComponent,
  ConnectionComponent,
  DatastoreComponent,
  ComponentSubscription,
} from "@/types/component-types";
import {SYSTEM_SERVICES, SYSTEM_CONNECTIONS} from "./system-components";
import type {ProductComponents} from "./products";

const apiBase = import.meta.env.VITE_API_URL;

// Visible = subscribed ∪ owned. The owned half covers the publisher dogfooding case —
// a publisher's own product isn't a subscription row but they still need its components.
interface VisibleProductSummary {
  id: string;
  name: string;
}

export interface OrgComponents {
  services: ServiceComponent[];
  connections: ConnectionComponent[];
  datastores: DatastoreComponent[];
  subscriptions: ComponentSubscription[];
}

const EMPTY: OrgComponents = {services: [], connections: [], datastores: [], subscriptions: []};

// Connection slots reference their template by opaque id; this maps id → display name so palette
// cards show "GitHub", not a raw template id. Best-effort — falls back to the id if unavailable.
async function fetchConnectionTemplateNames(): Promise<Map<string, string>> {
  try {
    const templates = await fetchWrapper.get<{id: string; name: string}[]>(`${apiBase}/connection-templates`);
    return new Map(templates.map((t) => [t.id, t.name]));
  } catch {
    return new Map();
  }
}

async function fetchOrgComponents(orgId: string): Promise<OrgComponents> {
  let visible: VisibleProductSummary[];
  try {
    visible = await fetchWrapper.get<VisibleProductSummary[]>(`${apiBase}/products/visible-to-org/${orgId}`);
  } catch {
    return {...EMPTY, services: [...SYSTEM_SERVICES], connections: [...SYSTEM_CONNECTIONS]};
  }

  const services: ServiceComponent[] = [...SYSTEM_SERVICES];
  const connections: ConnectionComponent[] = [...SYSTEM_CONNECTIONS];
  const datastores: DatastoreComponent[] = [];
  const subscriptions: ComponentSubscription[] = [];

  const templateNameById = await fetchConnectionTemplateNames();

  // Fan-out per product — serial would dominate initial-load time for orgs with many subscriptions.
  const fetched = await Promise.all(
    (visible ?? []).map(async (product) => {
      try {
        const components = await fetchWrapper.get<ProductComponents>(`${apiBase}/products/${product.id}/components`);
        return {product, components};
      } catch {
        // Skip products we can't load — the rest of the list still populates.
        return {product, components: null as ProductComponents | null};
      }
    })
  );

  for (const {product, components} of fetched) {
    if (!components) continue;
    const now = new Date().toISOString();

    for (const svc of components.services) {
      services.push({
        id: svc.serviceId,
        name: svc.serviceName,
        description: `${svc.serviceModelType} from ${svc.sourceWorkflowName}`,
        type: "service",
        source: "product",
        category: "Integrations",
        icon: "bi-gear",
        version: components.productVersionId,
        isGeneric: false,
        tags: [svc.serviceModelType.toLowerCase()],
        createdAt: now,
        updatedAt: now,
        productId: product.id,
        productName: product.name,
        productVersion: components.productVersionId,
        requiresSubscription: true,
        serviceType: svc.serviceModelType,
      });
    }

    for (const conn of components.connections) {
      const typeName = templateNameById.get(conn.connectionTemplateId) ?? conn.connectionTemplateId;
      connections.push({
        id: `${product.id}::${conn.slotName}`,
        name: conn.slotName,
        description: `${typeName} connection slot`,
        type: "connection",
        source: "product",
        category: "Integrations",
        icon: "bi-link",
        version: components.productVersionId,
        isGeneric: false,
        tags: [typeName.toLowerCase()],
        createdAt: now,
        updatedAt: now,
        productId: product.id,
        productName: product.name,
        productVersion: components.productVersionId,
        requiresSubscription: true,
        provider: typeName,
        connectionType: "api",
        authType: "api-key",
        configSchema: {type: "object", properties: {}},
      });
    }

    for (const ds of components.datastores) {
      datastores.push({
        id: `${product.id}::${ds.name}`,
        name: ds.name,
        description: ds.description || "Datastore",
        type: "datastore",
        source: "product",
        category: "Data & Storage",
        icon: "bi-database",
        version: components.productVersionId,
        isGeneric: false,
        tags: [],
        createdAt: now,
        updatedAt: now,
        productId: product.id,
        productName: product.name,
        productVersion: components.productVersionId,
        requiresSubscription: true,
        datastoreType: "nosql",
        provider: "json",
        capabilities: ["read", "write"],
      });
    }

    // Synthesize a subscription-shaped record so access-control logic upstream still resolves.
    // Always "active" — owned products have no subscription row, and visible-to-org only returns
    // active subscribed products anyway.
    subscriptions.push({
      id: `sub-${product.id}`,
      productId: product.id,
      productName: product.name,
      status: "active",
      subscribedDate: now,
      permissions: [
        ...components.services.map((s) => ({
          componentId: s.serviceId,
          componentType: "service" as const,
          accessLevel: "full" as const,
        })),
        ...components.connections.map((c) => ({
          componentId: `${product.id}::${c.slotName}`,
          componentType: "connection" as const,
          accessLevel: "full" as const,
        })),
        ...components.datastores.map((d) => ({
          componentId: `${product.id}::${d.name}`,
          componentType: "datastore" as const,
          accessLevel: "full" as const,
        })),
      ],
    });
  }

  return {services, connections, datastores, subscriptions};
}

export function useOrgComponents() {
  const sessionStore = useSessionStore();
  const orgId = computed(() => sessionStore.session?.currentOrgId as string | undefined);

  const {data, isFetching: componentsLoading, error: componentsError, refetch} = useQuery({
    queryKey: computed(() => ["org-components", orgId.value]),
    queryFn: () => fetchOrgComponents(orgId.value!),
    enabled: computed(() => !!orgId.value),
  });

  const components = computed<OrgComponents>(() => data.value ?? EMPTY);

  return {
    components,
    componentsLoading,
    componentsError: componentsError as Ref<ApiError | null>,
    refetchComponents: refetch,
  };
}
